import { type AppError, ErrorCode, FatalError } from '@imprivata-cloud/authn';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import type { BannerProps } from '../Banner/banner';
import OfflineIcon from '../../../assets/offline.svg?react';
import {
  IGNORED_ERROR_CODES,
  IGNORED_ERROR_MESSAGES,
  mapErrorToStatus,
} from '../../../utils/bannerUtils';
import { clearError } from '../../../store/actions';

export function useBanner() {
  const [banner, setBanner] = useState<BannerProps | undefined>(undefined);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const displayBanner = useCallback(
    (err: AppError) => {
      const noBanner =
        (err instanceof FatalError &&
          err.code !== ErrorCode.INVALID_TENANT_ID) ||
        (err.code && IGNORED_ERROR_CODES.includes(err.code)) ||
        (err.message && IGNORED_ERROR_MESSAGES.includes(err.message));

      if (noBanner) {
        setBanner(undefined);
        return;
      }

      const isOfflineError =
        err.code &&
        (err.code === ErrorCode.OFFLINE ||
          err.code === ErrorCode.NETWORK_ERROR);

      const subMessageKey = `errors.${err.code}-sub-message`;
      const subMessage = t(subMessageKey);
      const customIcon = isOfflineError ? () => <OfflineIcon /> : undefined;

      let message = err.code ? t(`errors.${err.code}`) : t(err.message ?? '');
      if (err.code && err.params?.username) {
        message = t(`errors.${err.code}`, {
          username: err.params?.username,
        });
      }

      setBanner({
        message,
        status: mapErrorToStatus(err),
        customIcon,
        subMessage: subMessage !== subMessageKey ? subMessage : undefined,
        clearBanner: () => {
          dispatch(clearError());
          setBanner(undefined);
        },
      });
    },
    [dispatch, t],
  );

  return { banner, setBanner, displayBanner };
}
